import React, { useState, useMemo } from 'react';
import { Theme, TextField, TextFieldProps, InputAdornment, Drawer } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import moment, { Moment } from 'moment-timezone';
import useShopFormat from 'hooks/useShopFormat';
import { RiArrowDownSFill } from 'react-icons/ri';
import TimeSelector from './TimeSelector';
import { OrderProduct, Duration } from 'common/types';
import { TimeSelectorArgs, TimeSelectorStyling } from 'components/TimeSelector/types';

export interface Props {
	selectorType: TimeSelectorArgs;
	date: string | null;
	setDate: (day: Moment) => void;
	orderProducts: OrderProduct[];
	startLocationId: string;
	endLocationId: string;
	duration: Duration;
	disabled?: boolean;
	label?: string;
	variant?: TextFieldProps['variant'];
	rightPositionedInput?: boolean;
	styling?: TimeSelectorStyling;
}

export const DateAndTimeSelector = (props: Props) => {
	const classes = useStyles();
	const {
		date,
		disabled,
		label,
		variant,
		rightPositionedInput,
		selectorType,
		orderProducts,
		startLocationId,
		endLocationId,
		duration,
		styling,
	} = props;
	const [editingDate, setEditingDate] = useState(false);
	const { localFormat } = useShopFormat();

	const handleDateChange = (newDate: moment.Moment) => {
		props.setDate(newDate);
	};

	const ignoredProductIds = orderProducts && orderProducts.map((product) => product.id);

	const minDate = useMemo(() => {
		const startOfToday = moment().startOf('day');
		const _date = moment(date);
		return _date.isBefore(startOfToday) ? _date : startOfToday;
	}, [date]);

	return (
		<>
			<div className={classes.relative}>
				<TextField
					label={label}
					id="start-date-input"
					onClick={() => !disabled && setEditingDate(true)}
					value={localFormat(moment(date).toISOString(), 'ddd. MMM D, HH:mm')}
					variant={variant ?? 'standard'}
					type="text"
					InputProps={{
						readOnly: true,
						...(variant !== 'outlined' && { disableUnderline: true }),
						classes: {
							input: rightPositionedInput ? classes.inputRight : classes.input,
						},
						endAdornment:
							selectorType.type === 'startTime' ? (
								<InputAdornment position="end">
									<RiArrowDownSFill color={'rgba(0, 0, 0, 0.54)'} size={22} />
								</InputAdornment>
							) : undefined,
					}}
					fullWidth
					InputLabelProps={{
						classes: rightPositionedInput
							? { root: classes.labelRoot, shrink: classes.shrink }
							: undefined,
					}}
					disabled={disabled}
				/>
			</div>
			{editingDate && date && (
				<Drawer anchor="bottom" open={editingDate}>
					<TimeSelector
						selectorType={selectorType}
						date={date}
						onDateChange={(date) => handleDateChange(date)}
						onClose={() => setEditingDate(false)}
						products={orderProducts}
						ignoredProductIds={ignoredProductIds}
						startLocationId={startLocationId}
						endLocationId={endLocationId}
						label={label}
						duration={duration}
						styling={styling}
						minDate={minDate}
					/>
				</Drawer>
			)}
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		relative: {
			position: 'relative',
		},
		tabs: {
			background: theme.palette.background.paper,
		},
		selectRoot: {
			width: 150,
		},
		datePickerContainer: {
			position: 'absolute',
			zIndex: 3000,
			minWidth: 300,
		},
		pickerLeft: {
			left: 0,
		},
		pickerRight: {
			right: 0,
		},
		timeContainer: {
			borderRadius: '2px',
			margin: 0,
			padding: 0,
			position: 'relative',
			display: 'flex',
			verticalAlign: 'middle',
			height: '100%',
			'& > *': {
				margin: 0,
			},
		},
		filledInput: {
			background: theme.palette.background.paper,
			borderRadius: 0,
		},
		textField: {
			width: '100%',
			background: theme.palette.background.paper,
		},
		input: {
			fontSize: '1.4rem',
			'&.Mui-disabled': {
				'&:hover': {
					cursor: 'default',
				},
			},
			'&:hover': {
				cursor: 'pointer',
			},
		},
		inputRight: {
			fontSize: '1.3rem',
			textAlign: 'right',
			'&.Mui-disabled': {
				color: theme.palette.text.secondary,
				opacity: 1,
				'&:hover': {
					cursor: 'default',
				},
			},
			'&:hover': {
				cursor: 'pointer',
			},
		},
		labelRoot: {
			right: 0,
			'&.Mui-disabled': {
				color: theme.palette.text.secondary,
				opacity: 1,
			},
		},
		shrink: {
			transformOrigin: 'top right',
			textAlign: 'right',
		},
		disableCursor: {
			cursor: 'default',
		},
	}),
);

export default DateAndTimeSelector;
